import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  ReferralPageWrapper, DentistSection, PatientSection, FormFooter, FormSection,
  SuccessScreen, RadioGroup, Fw, FieldError, icls,
  DENTIST_DEFAULTS, PATIENT_DEFAULTS, FOOTER_DEFAULTS,
  validateCommon, submitReferral,
} from './ReferralBase';

const TYPE_OPTIONS = [
  { value: 'simple', label: 'Simple extraction' },
  { value: 'surgical', label: 'Surgical extraction' },
  { value: 'wisdom', label: 'Wisdom tooth removal' },
  { value: 'multiple', label: 'Multiple extractions' },
  { value: 'sedation', label: 'Extraction under sedation' },
];

const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent (pain/infection)' },
  { value: 'pre_treatment', label: 'Pre-treatment (ortho/medical)' },
];

const DEFAULTS = {
  ...DENTIST_DEFAULTS, ...PATIENT_DEFAULTS,
  toothNumbers: '', extractionType: '',
  medicalHistory: '', clinicalSummary: '',
  urgency: '', specialInstructions: '',
  ...FOOTER_DEFAULTS,
};

function validate(form) {
  const errors = validateCommon(form);
  if (!form.toothNumbers.trim()) errors.toothNumbers = 'Required';
  if (!form.clinicalSummary.trim()) errors.clinicalSummary = 'Required';
  return errors;
}

export default function ExtractionsReferral() {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try { await submitReferral(form, 'Extractions'); } catch (_) {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const reset = () => { setForm(DEFAULTS); setErrors({}); setSubmitted(false); };
  const inputCls = (f) => icls(f, errors, focused);
  const commonProps = { form, errors, focused, onChange: handleChange, onFocus: setFocused, onBlur: () => setFocused(null) };

  return (
    <ReferralPageWrapper
      title="Extractions Referral"
      subtitle="Refer your patient for simple, surgical, or wisdom tooth extractions, including sedation cases."
      photo="/images/referral/referral-extractions.jpg">
      <h2 className="font-serif text-forest text-2xl font-semibold mb-1">Extractions Referral Form</h2>
      <p className="text-gray-500 text-sm mb-7">All fields marked <span className="text-red-400">*</span> are required.</p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessScreen key="success" patientName={form.patientName} onReset={reset} />
        ) : (
          <form key="form" onSubmit={handleSubmit} noValidate>
            <DentistSection {...commonProps} />
            <PatientSection {...commonProps} />

            <FormSection title="Clinical Information">
              <div className="grid gap-5">
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="toothNumbers">
                    Tooth / Site Numbers <span className="text-red-400">*</span>
                  </label>
                  <input id="toothNumbers" name="toothNumbers" type="text" value={form.toothNumbers}
                    onChange={handleChange} placeholder="e.g. LR8, UL8"
                    onFocus={() => setFocused('toothNumbers')} onBlur={() => setFocused(null)}
                    className={inputCls('toothNumbers')} />
                  <FieldError error={errors.toothNumbers} />
                </Fw>
                <RadioGroup name="extractionType" label="Type of Extraction"
                  options={TYPE_OPTIONS} value={form.extractionType}
                  onChange={handleChange} error={errors.extractionType} />
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="medicalHistory">
                    Relevant Medical History
                  </label>
                  <textarea id="medicalHistory" name="medicalHistory" rows={3} value={form.medicalHistory}
                    onChange={handleChange} placeholder="Anticoagulants, blood disorders, bisphosphonates, allergies…"
                    onFocus={() => setFocused('medicalHistory')} onBlur={() => setFocused(null)}
                    className={`${inputCls('medicalHistory')} resize-none`} />
                </Fw>
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="clinicalSummary">
                    Clinical Summary <span className="text-red-400">*</span>
                  </label>
                  <textarea id="clinicalSummary" name="clinicalSummary" rows={4} value={form.clinicalSummary}
                    onChange={handleChange} placeholder="Reason for referral, relevant clinical findings, any previous treatment attempts…"
                    onFocus={() => setFocused('clinicalSummary')} onBlur={() => setFocused(null)}
                    className={`${inputCls('clinicalSummary')} resize-none`} />
                  <FieldError error={errors.clinicalSummary} />
                </Fw>
                <RadioGroup name="urgency" label="Urgency"
                  options={URGENCY_OPTIONS} value={form.urgency}
                  onChange={handleChange} error={errors.urgency} />
              </div>
            </FormSection>

            <FormSection title="Additional Information">
              <Fw>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="specialInstructions">
                  Special Instructions
                </label>
                <textarea id="specialInstructions" name="specialInstructions" rows={3} value={form.specialInstructions}
                  onChange={handleChange} placeholder="Any other relevant information…"
                  onFocus={() => setFocused('specialInstructions')} onBlur={() => setFocused(null)}
                  className={`${inputCls('specialInstructions')} resize-none`} />
              </Fw>
            </FormSection>

            <FormFooter {...commonProps} submitting={submitting} />
          </form>
        )}
      </AnimatePresence>
    </ReferralPageWrapper>
  );
}
